import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Rating, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import {
  CHAT_FEEDBACK_COMMENT_MAX_CHARS,
  CHAT_FEEDBACK_RESOLUTION_LABEL,
  ChatFeedback,
  ChatFeedbackInput,
  ChatFeedbackResolution,
} from '../../pages/app/support-agent/support-agent-api';

/**
 * Feedback card (Michael, 10/20; design canvas "Feedback — one card in the
 * chat after it closes"; pom-2-api #597): ONE assistant message under the
 * resolved line with every question visible at once. "Send feedback" turns
 * on only once everything but the text box is answered; the text box is the
 * only optional part. After sending, the card locks to the answers the
 * server stored and the assistant says thanks.
 *
 * Whether to show it is the SERVER's call (`ChatDoc.feedbackEligible`) —
 * the widget never re-derives the rule. The support-team stars are asked
 * exactly when a person joined the chat (`escalation` set), which is also
 * what pom-2-api requires of the body.
 */

export const AI_CHAT_FEEDBACK_RESOLVED_QUESTION = 'Was your issue resolved?';
export const AI_CHAT_FEEDBACK_ASSISTANT_QUESTION = 'How helpful was the assistant?';
export const AI_CHAT_FEEDBACK_SUPPORT_QUESTION = 'How helpful was our support team?';
// "(optional)" lives in the LABEL, not the placeholder (Michael, 10/20): MUI
// hides an outlined field's placeholder until it is focused, so a placeholder
// was the only cue and it was invisible at rest — and not read out either.
export const AI_CHAT_FEEDBACK_COMMENT_LABEL = 'What could we improve? (optional)';
// The locked card shows the question only — "(optional)" is a cue for the
// form, not a caption for an answer already given.
export const AI_CHAT_FEEDBACK_COMMENT_SENT_LABEL = 'What could we improve?';
export const AI_CHAT_FEEDBACK_COMMENT_PLACEHOLDER = 'Tell us more';
export const AI_CHAT_FEEDBACK_SEND = 'Send feedback';
export const AI_CHAT_FEEDBACK_SENT = 'Sent';
export const AI_CHAT_FEEDBACK_THANKS = 'Thanks for the feedback. It helps us improve.';
export const AI_CHAT_FEEDBACK_ERROR_COPY = "Couldn't send your feedback. Please try again.";

const RESOLUTIONS: ChatFeedbackResolution[] = ['fully', 'partially', 'not_at_all'];

export interface AiChatFeedbackProps {
  /** `ChatDoc.feedbackEligible` — the card renders only on true, or once `feedback` exists. */
  eligible: boolean;
  /** The stored answers; when set the card is locked. */
  feedback?: ChatFeedback;
  /** A person joined the chat: ask for the support-team stars too. */
  supportJoined: boolean;
  /**
   * Sends the answers; resolves, once the widget applied the server's
   * response, to the stored feedback (the card is now locked) or undefined
   * when the server has none. Throws on failure.
   */
  onSubmit: (input: ChatFeedbackInput) => Promise<ChatFeedback | undefined>;
}

/** The exact rule for the Send button, exported for tests. */
export function feedbackReadyToSend(
  draft: { resolution: ChatFeedbackResolution | null; assistantRating: number | null; supportRating: number | null },
  supportJoined: boolean,
): boolean {
  return draft.resolution !== null && draft.assistantRating !== null && (!supportJoined || draft.supportRating !== null);
}

function Stars({
  label,
  testId,
  value,
  onChange,
  readOnly,
}: {
  label: string;
  testId: string;
  value: number | null;
  onChange?: (next: number | null) => void;
  readOnly?: boolean;
}) {
  return (
    <Box data-testid={testId}>
      <Typography variant="body2" sx={{ fontWeight: 600 }} id={`${testId}-label`}>
        {label}
      </Typography>
      {/* MUI's Rating clears when the selected star is clicked again — the canvas's rule. */}
      {/* Screen readers (Michael, 10/20): editable, MUI renders a role-less
          span, so name it as a radiogroup after its question — otherwise the
          assistant and support stars both announce only "N Stars". Read-only,
          MUI renders role="img" named after the value ("4 Stars"); an
          aria-labelledby there would override that name and hide the answer. */}
      <Rating
        name={testId}
        value={value}
        readOnly={readOnly}
        onChange={(_, next) => onChange?.(next)}
        {...(readOnly ? {} : { role: 'radiogroup', 'aria-labelledby': `${testId}-label` })}
        size="large"
      />
    </Box>
  );
}

export const AiChatFeedback: React.FC<AiChatFeedbackProps> = ({ eligible, feedback, supportJoined, onSubmit }) => {
  const [resolution, setResolution] = useState<ChatFeedbackResolution | null>(null);
  const [assistantRating, setAssistantRating] = useState<number | null>(null);
  const [supportRating, setSupportRating] = useState<number | null>(null);
  const [comment, setComment] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Keyboard focus after Send (Michael, 10/20): the Send button is what has
  // focus when the card locks, and it unmounts with the form, which drops
  // focus to <body> — the next Tab would leave the chat panel entirely. Move
  // it to the locked card ONLY when this card's own Send caused the lock;
  // a locked card arriving from the server (restore/refresh) must not steal
  // focus from whatever the customer was doing.
  const lockedRef = useRef<HTMLDivElement | null>(null);
  const justSentRef = useRef(false);
  useEffect(() => {
    if (feedback && justSentRef.current) {
      justSentRef.current = false;
      lockedRef.current?.focus();
    }
  }, [feedback]);

  if (feedback) {
    // Locked: the answers the server stored, then the thanks.
    return (
      <Stack spacing={1} data-testid="ai-chat-feedback-sent" ref={lockedRef} tabIndex={-1} sx={{ outline: 'none' }}>
        <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'action.hover' }}>
          <Stack spacing={1}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                {AI_CHAT_FEEDBACK_RESOLVED_QUESTION}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {CHAT_FEEDBACK_RESOLUTION_LABEL[feedback.resolution]}
              </Typography>
            </Box>
            <Stars label={AI_CHAT_FEEDBACK_ASSISTANT_QUESTION} testId="ai-chat-feedback-assistant-sent" value={feedback.assistantRating} readOnly />
            {feedback.supportRating !== undefined && (
              <Stars label={AI_CHAT_FEEDBACK_SUPPORT_QUESTION} testId="ai-chat-feedback-support-sent" value={feedback.supportRating} readOnly />
            )}
            {feedback.comment && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {AI_CHAT_FEEDBACK_COMMENT_SENT_LABEL}
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {feedback.comment}
                </Typography>
              </Box>
            )}
            <Typography variant="caption" color="success.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontWeight: 600 }}>
              <CheckIcon fontSize="inherit" /> {AI_CHAT_FEEDBACK_SENT}
            </Typography>
          </Stack>
        </Box>
        <Typography variant="body2" sx={{ p: 1.5, borderRadius: 2, bgcolor: 'action.hover', alignSelf: 'flex-start' }}>
          {AI_CHAT_FEEDBACK_THANKS}
        </Typography>
      </Stack>
    );
  }

  if (!eligible) return null;

  const ready = feedbackReadyToSend({ resolution, assistantRating, supportRating }, supportJoined) && !sending;

  const submit = async () => {
    if (!ready || resolution === null || assistantRating === null) return;
    setSending(true);
    setError(null);
    try {
      const trimmed = comment.trim();
      // Set BEFORE the await: the parent applies `feedback` while this
      // awaits, and the focus effect may run before this continuation.
      justSentRef.current = true;
      const locked = await onSubmit({
        resolution,
        assistantRating,
        ...(supportJoined && supportRating !== null ? { supportRating } : {}),
        ...(trimmed ? { comment: trimmed } : {}),
      });
      // No lock from this send (a 409 re-read that came back still open):
      // a lock arriving later from the server must not take focus.
      if (!locked) justSentRef.current = false;
    } catch {
      // The answers stay in the form so the customer can just tap Send again.
      justSentRef.current = false;
      setError(AI_CHAT_FEEDBACK_ERROR_COPY);
    } finally {
      setSending(false);
    }
  };

  return (
    <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'action.hover' }} data-testid="ai-chat-feedback">
      <Stack spacing={1.5}>
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }} id="ai-chat-feedback-resolved-label">
            {AI_CHAT_FEEDBACK_RESOLVED_QUESTION}
          </Typography>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={resolution}
            onChange={(_, next: ChatFeedbackResolution | null) => setResolution(next)}
            aria-labelledby="ai-chat-feedback-resolved-label"
          >
            {RESOLUTIONS.map((value) => (
              <ToggleButton key={value} value={value} sx={{ textTransform: 'none' }}>
                {CHAT_FEEDBACK_RESOLUTION_LABEL[value]}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
        <Stars label={AI_CHAT_FEEDBACK_ASSISTANT_QUESTION} testId="ai-chat-feedback-assistant" value={assistantRating} onChange={setAssistantRating} />
        {supportJoined && (
          <Stars label={AI_CHAT_FEEDBACK_SUPPORT_QUESTION} testId="ai-chat-feedback-support" value={supportRating} onChange={setSupportRating} />
        )}
        <TextField
          label={AI_CHAT_FEEDBACK_COMMENT_LABEL}
          placeholder={AI_CHAT_FEEDBACK_COMMENT_PLACEHOLDER}
          value={comment}
          onChange={(e) => setComment(e.target.value.slice(0, CHAT_FEEDBACK_COMMENT_MAX_CHARS))}
          multiline
          minRows={2}
          maxRows={4}
          size="small"
          fullWidth
          inputProps={{ maxLength: CHAT_FEEDBACK_COMMENT_MAX_CHARS, 'data-testid': 'ai-chat-feedback-comment' }}
        />
        {error && <Alert severity="error">{error}</Alert>}
        <Button variant="contained" onClick={submit} disabled={!ready} data-testid="ai-chat-feedback-send">
          {AI_CHAT_FEEDBACK_SEND}
        </Button>
      </Stack>
    </Box>
  );
};
